import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

import { useGetUserAccessQuery } from "state/rtkQuery/access/accessQueries";
import UnauthorizedAccess from "./UnauthorizedAccess";

// "Leadership" is NOT a global role — unlike EnterpriseAdmin/OperationsAdmin
// (whose org_id is null, since they're the only truly platform-wide roles),
// Leadership always carries a real org_id: it's granted per organization,
// same as OrganizationOwner/OrganizationApprover (see useEffectiveOrgRole,
// which already reads this correctly for org-scoped pages). The global
// roles[] list is the wrong data source for this check — it has no org
// context at all — so this guard reads the org-scoped list instead and asks
// "is this person Leadership in AT LEAST ONE org," which is enough to earn a
// spot on the (enterprise-wide) Enterprise Usage page — it does not scope
// what that page then shows them.
export const isLeadershipInAnyOrg = (organizations) =>
  Array.isArray(organizations) && organizations.some((org) => org.role === "Leadership");

const RequireLeadershipRole = () => {
  const location = useLocation();
  const authToken = useSelector((state) => state.auth?.authToken);
  const email = useSelector((state) => state.auth?.userAccount?.username);
  const { data, isLoading } = useGetUserAccessQuery({ email }, { skip: !email });

  if (!authToken) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // isLoading only (not isFetching) — see useAccessPermissions.js's note:
  // gating this guard's <Outlet /> on isFetching unmounts and remounts the
  // routed page on every background refetch (e.g. any mutation elsewhere
  // invalidating the same query), wiping its local state for no reason.
  if (isLoading || data === undefined) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!isLeadershipInAnyOrg(data?.organization)) {
    return (
      <UnauthorizedAccess message="This page is only available to Leadership members. Please return to the home dashboard." />
    );
  }

  return <Outlet />;
};

export default RequireLeadershipRole;
